import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { X, ChevronDown, ChevronUp, Code, ArrowLeft, ArrowRight, GitBranch, Cpu, Zap, Home, ExternalLink, Play, RotateCcw, MapPin, Layers, Navigation } from 'lucide-react';
import { BRAIN_MODULES, MODULE_CONNECTIONS, CONNECTION_TYPES, TOKEN_FLOW_SUMMARY } from '../../data/brainModules';

/**
 * Immersive Deep-Dive View V3.0 - Fluid Connected Flow
 *
 * New Features:
 * - Breadcrumb trail showing navigation path
 * - "Follow the Flow" guided tour through data pipeline
 * - Quick Navigate available at ALL layers (not just Layer 1)
 * - Smooth zoom in/out with preview of connected modules
 * - Data flow visualization showing the full pipeline
 */

// The canonical data flow order through the system
const DATA_FLOW_ORDER = [
  'language-sentiment',  // Step 1: Detect language & sentiment
  'core-identity',       // Step 2: Establish who you are
  'reasoning',           // Step 3: Analyze the review
  'anti-sycophancy',     // Step 4: Apply sovereignty rules
  'anti-echo',           // Step 5: Prevent parroting
  'tone-instructions',   // Step 6: Set the voice
  'smart-brackets',      // Step 7: Add user choices
  'few-shot-examples',   // Step 8: Provide examples
  'avoid-patterns',      // Step 9: Filter AI slop
  'industry-context',    // Step 10: Industry specifics
];

const LayerView = ({ module, onClose, onNavigateToModule }) => {
  const [currentLayer, setCurrentLayer] = useState(1);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [transitionDirection, setTransitionDirection] = useState('in');
  const [showConnections, setShowConnections] = useState(false);
  const [showTokens, setShowTokens] = useState(false);
  const [showFlowGuide, setShowFlowGuide] = useState(false);
  const [navigationHistory, setNavigationHistory] = useState([{ module, layer: 1 }]);
  const [isAutoPlaying, setIsAutoPlaying] = useState(false);

  const totalLayers = Object.keys(module.layers).length;
  const layer = module.layers[currentLayer];

  // Get position in data flow
  const flowPosition = DATA_FLOW_ORDER.indexOf(module.id);
  const nextInFlow = flowPosition >= 0 && flowPosition < DATA_FLOW_ORDER.length - 1
    ? BRAIN_MODULES.find(m => m.id === DATA_FLOW_ORDER[flowPosition + 1])
    : null;
  const prevInFlow = flowPosition > 0
    ? BRAIN_MODULES.find(m => m.id === DATA_FLOW_ORDER[flowPosition - 1])
    : null;

  // Get connection data for this module
  const connections = useMemo(() => {
    const conn = MODULE_CONNECTIONS[module.id] || { outgoing: [], incoming: [], tokens: {} };
    return conn;
  }, [module.id]);

  // Get all related modules (both incoming and outgoing)
  const relatedModules = useMemo(() => {
    const related = [];
    const seen = new Set();

    // Outgoing connections
    connections.outgoing?.forEach(c => {
      if (!seen.has(c.to)) {
        const targetModule = BRAIN_MODULES.find(m => m.id === c.to);
        if (targetModule) {
          related.push({ ...c, module: targetModule, direction: 'outgoing' });
          seen.add(c.to);
        }
      }
    });

    // Incoming connections
    connections.incoming?.forEach(c => {
      if (!seen.has(c.from)) {
        const sourceModule = BRAIN_MODULES.find(m => m.id === c.from);
        if (sourceModule) {
          related.push({ ...c, module: sourceModule, direction: 'incoming' });
          seen.add(c.from);
        }
      }
    });

    return related;
  }, [connections, module.id]);

  // Depth-based styling
  const depthIntensity = currentLayer / totalLayers;

  const goDeeper = useCallback(() => {
    if (currentLayer < totalLayers && !isTransitioning) {
      setTransitionDirection('in');
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentLayer(prev => prev + 1);
        setNavigationHistory(prev => [...prev.slice(0, -1), { module, layer: currentLayer + 1 }]);
        setTimeout(() => setIsTransitioning(false), 300);
      }, 300);
    }
  }, [currentLayer, totalLayers, isTransitioning, module]);

  const goBack = useCallback(() => {
    if (currentLayer > 1 && !isTransitioning) {
      setTransitionDirection('out');
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentLayer(prev => prev - 1);
        setNavigationHistory(prev => [...prev.slice(0, -1), { module, layer: currentLayer - 1 }]);
        setTimeout(() => setIsTransitioning(false), 300);
      }, 300);
    }
  }, [currentLayer, isTransitioning, module]);

  const navigateToModule = useCallback((targetModule, targetLayer = 1) => {
    if (isTransitioning) return;
    setTransitionDirection('sideways');
    setIsTransitioning(true);

    // Add to navigation history
    setNavigationHistory(prev => [...prev, { module: targetModule, layer: targetLayer }]);

    setTimeout(() => {
      if (onNavigateToModule) {
        onNavigateToModule(targetModule);
      }
      setCurrentLayer(targetLayer);
      setTimeout(() => setIsTransitioning(false), 300);
    }, 300);
  }, [isTransitioning, onNavigateToModule]);

  const navigateToRelated = useCallback((relatedModule) => {
    navigateToModule(relatedModule.module, 1);
  }, [navigateToModule]);

  // Navigate back in history
  const goBackInHistory = useCallback(() => {
    if (navigationHistory.length > 1 && !isTransitioning) {
      const newHistory = [...navigationHistory];
      newHistory.pop(); // Remove current
      const previous = newHistory[newHistory.length - 1];

      setTransitionDirection('out');
      setIsTransitioning(true);

      setTimeout(() => {
        setNavigationHistory(newHistory);
        if (previous.module.id !== module.id && onNavigateToModule) {
          onNavigateToModule(previous.module);
        }
        setCurrentLayer(previous.layer);
        setTimeout(() => setIsTransitioning(false), 300);
      }, 300);
    }
  }, [navigationHistory, isTransitioning, module.id, onNavigateToModule]);

  // Follow the flow - go to next module in data pipeline
  const followFlow = useCallback(() => {
    if (nextInFlow && !isTransitioning) {
      navigateToModule(nextInFlow, 1);
    }
  }, [nextInFlow, isTransitioning, navigateToModule]);

  // Go to previous in flow
  const goBackInFlow = useCallback(() => {
    if (prevInFlow && !isTransitioning) {
      navigateToModule(prevInFlow, 1);
    }
  }, [prevInFlow, isTransitioning, navigateToModule]);

  // Jump to any layer directly
  const jumpToLayer = useCallback((targetLayer) => {
    if (targetLayer !== currentLayer && !isTransitioning) {
      setTransitionDirection(targetLayer > currentLayer ? 'in' : 'out');
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentLayer(targetLayer);
        setNavigationHistory(prev => [...prev.slice(0, -1), { module, layer: targetLayer }]);
        setTimeout(() => setIsTransitioning(false), 300);
      }, 300);
    }
  }, [currentLayer, isTransitioning, module]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowDown' || e.key === ' ') {
        e.preventDefault();
        goDeeper();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        goBack();
      } else if (e.key === 'Backspace') {
        e.preventDefault();
        goBackInHistory();
      } else if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        if (prevInFlow) goBackInFlow();
        else if (relatedModules.length > 0) navigateToRelated(relatedModules[0]);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        if (nextInFlow) followFlow();
        else if (relatedModules.length > 1) navigateToRelated(relatedModules[1]);
      } else if (e.key === 'c' || e.key === 'C') {
        setShowConnections(prev => !prev);
      } else if (e.key === 't' || e.key === 'T') {
        setShowTokens(prev => !prev);
      } else if (e.key === 'f' || e.key === 'F') {
        setShowFlowGuide(prev => !prev);
      } else if (e.key >= '1' && e.key <= '8') {
        const targetLayer = parseInt(e.key);
        if (targetLayer <= totalLayers) {
          jumpToLayer(targetLayer);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentLayer, isTransitioning, relatedModules, goDeeper, goBack, goBackInHistory, followFlow, goBackInFlow, navigateToRelated, jumpToLayer, totalLayers, nextInFlow, prevInFlow, onClose]);

  // Layer type display names
  const layerNames = {
    headline: 'THE HEADLINE',
    tagline: 'THE TAGLINE',
    problem: 'THE PROBLEM',
    psychology: 'THE PSYCHOLOGY',
    solution: 'THE SOLUTION',
    rules: 'THE RULES',
    example: 'BEFORE & AFTER',
    code: 'THE CODE'
  };

  // Render content based on layer type
  const renderContent = () => {
    if (layer.type === 'rules' && Array.isArray(layer.content)) {
      return (
        <ul style={styles.rulesList}>
          {layer.content.map((rule, idx) => (
            <li key={idx} style={styles.ruleItem}>
              <span style={{ ...styles.ruleBullet, backgroundColor: module.color }}></span>
              {rule}
            </li>
          ))}
        </ul>
      );
    }

    if (layer.type === 'example') {
      return (
        <div style={styles.exampleContainer}>
          <div style={styles.exampleBox('bad')}>
            <div style={styles.exampleLabel('bad')}>TYPICAL AI</div>
            <p style={styles.exampleText}>{layer.before}</p>
          </div>
          <div style={styles.exampleArrow}>
            <ChevronDown size={32} color={module.color} />
          </div>
          <div style={styles.exampleBox('good')}>
            <div style={styles.exampleLabel('good')}>REVIEWRESPONDER</div>
            <p style={styles.exampleText}>{layer.after}</p>
          </div>
        </div>
      );
    }

    if (layer.type === 'code') {
      return (
        <div style={styles.codeContainer}>
          <div style={styles.codeHeader}>
            <Code size={16} color="#9ca3af" />
            <span>{layer.functionName}</span>
          </div>
          <pre style={styles.codeBlock}>
            <code>{layer.content}</code>
          </pre>
        </div>
      );
    }

    return <p style={styles.contentText}>{layer.content}</p>;
  };

  // Render breadcrumb trail - ALWAYS shows "Brain Overview" as root
  const renderBreadcrumbs = () => {
    return (
      <div style={styles.breadcrumbs}>
        {/* Brain Overview - ALWAYS visible as root */}
        <button onClick={onClose} style={styles.breadcrumbHome}>
          <ArrowLeft size={14} />
          <Home size={14} />
          <span>Brain Overview</span>
        </button>
        {navigationHistory.length > 0 && navigationHistory.slice(-3).map((item, idx) => (
          <React.Fragment key={idx}>
            <ChevronRight size={14} color="#4b5563" />
            <button
              onClick={() => {
                if (idx < navigationHistory.slice(-3).length - 1) {
                  // Navigate back to this point
                  const targetIdx = navigationHistory.length - 3 + idx;
                  const newHistory = navigationHistory.slice(0, targetIdx + 1);
                  const target = newHistory[newHistory.length - 1];
                  setNavigationHistory(newHistory);
                  if (target.module.id !== module.id && onNavigateToModule) {
                    onNavigateToModule(target.module);
                  }
                  setCurrentLayer(target.layer);
                }
              }}
              style={{
                ...styles.breadcrumbItem,
                color: idx === navigationHistory.slice(-3).length - 1 ? '#e5e7eb' : '#6b7280',
              }}
            >
              <span>{item.module.icon}</span>
              <span>{item.module.friendlyTitle.split(' ').slice(0, 2).join(' ')}</span>
              {item.layer > 1 && <span style={styles.breadcrumbLayer}>L{item.layer}</span>}
            </button>
          </React.Fragment>
        ))}
      </div>
    );
  };

  // Render data flow guide
  const renderFlowGuide = () => {
    if (!showFlowGuide) return null;

    return (
      <div style={styles.flowGuide}>
        <div style={styles.flowGuideHeader}>
          <Navigation size={16} />
          <span>Data Flow Pipeline</span>
          <button onClick={() => setShowFlowGuide(false)} style={styles.flowGuideClose}>
            <X size={14} />
          </button>
        </div>
        <div style={styles.flowGuideContent}>
          {DATA_FLOW_ORDER.map((moduleId, idx) => {
            const flowModule = BRAIN_MODULES.find(m => m.id === moduleId);
            if (!flowModule) return null;
            const isCurrent = moduleId === module.id;
            const isPast = idx < flowPosition;

            return (
              <button
                key={moduleId}
                onClick={() => !isCurrent && navigateToModule(flowModule, 1)}
                style={{
                  ...styles.flowStep,
                  backgroundColor: isCurrent ? `${flowModule.color}30` : isPast ? 'rgba(34, 197, 94, 0.1)' : 'transparent',
                  borderColor: isCurrent ? flowModule.color : isPast ? '#22c55e' : '#374151',
                  cursor: isCurrent ? 'default' : 'pointer',
                }}
              >
                <span style={styles.flowStepNumber(isCurrent, isPast)}>{idx + 1}</span>
                <span style={styles.flowStepIcon}>{flowModule.icon}</span>
                <span style={{
                  ...styles.flowStepTitle,
                  color: isCurrent ? '#fff' : isPast ? '#86efac' : '#9ca3af',
                }}>
                  {flowModule.friendlyTitle.split(' ').slice(0, 2).join(' ')}
                </span>
                {isCurrent && <span style={styles.currentIndicator}>← You are here</span>}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  // Render related modules panel
  const renderRelatedModules = () => {
    if (relatedModules.length === 0) return null;

    return (
      <div style={styles.relatedPanel(showConnections)}>
        <button
          onClick={() => setShowConnections(!showConnections)}
          style={styles.panelToggle(module.color)}
        >
          <GitBranch size={16} />
          <span>Connections ({relatedModules.length})</span>
          {showConnections ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>

        {showConnections && (
          <div style={styles.relatedList}>
            {relatedModules.map((related, idx) => {
              const connType = CONNECTION_TYPES[related.type] || {};
              return (
                <button
                  key={idx}
                  onClick={() => navigateToRelated(related)}
                  style={styles.relatedItem(related.module.color, related.direction)}
                >
                  <div style={styles.relatedHeader}>
                    <span style={styles.relatedIcon}>{related.module.icon}</span>
                    <div style={styles.relatedInfo}>
                      <span style={styles.relatedTitle}>{related.module.friendlyTitle}</span>
                      <span style={styles.relatedDirection}>
                        {related.direction === 'outgoing' ? (
                          <><ArrowRight size={12} /> flows to</>
                        ) : (
                          <><ArrowLeft size={12} /> receives from</>
                        )}
                      </span>
                    </div>
                    <ExternalLink size={14} color="#6b7280" />
                  </div>
                  <div style={styles.relatedDescription}>
                    <span style={{ ...styles.connectionBadge, backgroundColor: `${connType.color}30`, color: connType.color }}>
                      {connType.label}
                    </span>
                    <span style={styles.dataFlow}>{related.dataFlow}</span>
                  </div>
                  <p style={styles.relatedDesc}>{related.description}</p>
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // Render token flow panel
  const renderTokenFlow = () => {
    const tokens = connections.tokens || {};
    if (!tokens.input && !tokens.output) return null;

    return (
      <div style={styles.tokenPanel(showTokens)}>
        <button
          onClick={() => setShowTokens(!showTokens)}
          style={styles.panelToggle(module.color)}
        >
          <Cpu size={16} />
          <span>Token Flow</span>
          {showTokens ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>

        {showTokens && (
          <div style={styles.tokenContent}>
            {tokens.process && (
              <div style={styles.tokenSection}>
                <div style={styles.tokenLabel}>
                  <Zap size={14} color={module.color} />
                  PROCESS
                </div>
                <p style={styles.tokenValue}>{tokens.process}</p>
              </div>
            )}

            {tokens.input && tokens.input.length > 0 && (
              <div style={styles.tokenSection}>
                <div style={styles.tokenLabel}>
                  <ArrowRight size={14} color="#22c55e" />
                  INPUT TOKENS
                </div>
                <div style={styles.tokenList}>
                  {tokens.input.map((t, i) => (
                    <span key={i} style={styles.tokenChip('input')}>{t}</span>
                  ))}
                </div>
              </div>
            )}

            {tokens.output && tokens.output.length > 0 && (
              <div style={styles.tokenSection}>
                <div style={styles.tokenLabel}>
                  <ArrowLeft size={14} color="#3b82f6" />
                  OUTPUT TOKENS
                </div>
                <div style={styles.tokenList}>
                  {tokens.output.map((t, i) => (
                    <span key={i} style={styles.tokenChip('output')}>{t}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // Render layer quick-jump
  const renderLayerJump = () => (
    <div style={styles.layerJump}>
      <Layers size={14} color="#6b7280" />
      {[...Array(totalLayers)].map((_, i) => (
        <button
          key={i}
          onClick={() => jumpToLayer(i + 1)}
          style={{
            ...styles.layerJumpButton,
            backgroundColor: i + 1 === currentLayer ? module.color : 'transparent',
            borderColor: i + 1 === currentLayer ? module.color : '#374151',
            color: i + 1 === currentLayer ? '#fff' : '#6b7280',
          }}
          title={`Layer ${i + 1}: ${layerNames[module.layers[i + 1]?.type] || `Layer ${i + 1}`}`}
        >
          {i + 1}
        </button>
      ))}
    </div>
  );

  return (
    <div style={styles.overlay}>
      {/* Animated background particles */}
      <div style={styles.particleField(module.color, depthIntensity)}>
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            style={{
              ...styles.particle,
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${3 + Math.random() * 4}s`,
              backgroundColor: module.color,
              opacity: 0.1 + Math.random() * 0.3,
            }}
          />
        ))}
      </div>

      {/* Depth rings */}
      <div style={styles.depthRings}>
        {[...Array(currentLayer)].map((_, i) => (
          <div
            key={i}
            style={{
              ...styles.depthRing,
              width: `${100 + i * 150}%`,
              height: `${100 + i * 150}%`,
              borderColor: `${module.color}${Math.floor(20 - i * 2).toString(16).padStart(2, '0')}`,
              animationDelay: `${i * 0.2}s`,
            }}
          />
        ))}
      </div>

      {/* Top navigation bar */}
      <div style={styles.topNav}>
        {/* Breadcrumbs */}
        {renderBreadcrumbs()}

        {/* Right side controls */}
        <div style={styles.topNavRight}>
          {/* Flow guide toggle */}
          <button
            onClick={() => setShowFlowGuide(!showFlowGuide)}
            style={{
              ...styles.topNavButton,
              backgroundColor: showFlowGuide ? `${module.color}30` : 'rgba(31, 41, 55, 0.8)',
            }}
            title="Show Data Flow (F)"
          >
            <Navigation size={16} />
            <span>Flow</span>
          </button>

          {/* Close button (X) */}
          <button onClick={onClose} style={styles.closeButton} title="Close (Esc)">
            <X size={20} />
          </button>
        </div>
      </div>

      {/* Flow guide panel */}
      {renderFlowGuide()}

      {/* Back in history button */}
      {navigationHistory.length > 1 && (
        <button onClick={goBackInHistory} style={styles.historyBackButton} title="Go back (Backspace)">
          <RotateCcw size={16} />
          <span>Back</span>
        </button>
      )}

      {/* Layer back button */}
      {currentLayer > 1 && (
        <button onClick={goBack} style={styles.backButton}>
          <ChevronUp size={20} />
          <span>Layer {currentLayer - 1}</span>
        </button>
      )}

      {/* Depth indicator with quick-jump */}
      <div style={styles.depthIndicator}>
        <div style={styles.depthTrack}>
          {[...Array(totalLayers)].map((_, i) => (
            <button
              key={i}
              onClick={() => jumpToLayer(i + 1)}
              style={{
                ...styles.depthDot,
                backgroundColor: i < currentLayer ? module.color : '#374151',
                transform: i + 1 === currentLayer ? 'scale(1.5)' : 'scale(1)',
                boxShadow: i + 1 === currentLayer ? `0 0 10px ${module.color}` : 'none',
                cursor: 'pointer',
              }}
              title={`Layer ${i + 1}`}
            />
          ))}
        </div>
        <span style={styles.depthLabel}>Layer {currentLayer} of {totalLayers}</span>
      </div>

      {/* Left Panel - Related Modules (Connections) */}
      <div style={styles.leftPanel}>
        {renderRelatedModules()}
        {renderTokenFlow()}
      </div>

      {/* Main content area */}
      <div
        style={{
          ...styles.contentArea,
          transform: isTransitioning
            ? transitionDirection === 'in'
              ? 'scale(0.8) translateY(50px)'
              : transitionDirection === 'out'
              ? 'scale(1.2) translateY(-50px)'
              : 'translateX(-100px) scale(0.9)'
            : 'scale(1) translateY(0)',
          opacity: isTransitioning ? 0 : 1,
        }}
      >
        {/* Module icon */}
        <div style={styles.moduleIcon(module.color)}>
          {module.icon}
        </div>

        {/* Module title */}
        <div style={styles.moduleTitle}>{module.friendlyTitle}</div>

        {/* Layer type label */}
        <div style={styles.layerType(module.color)}>
          {layerNames[layer.type] || `LAYER ${currentLayer}`}
        </div>

        {/* Content */}
        <div style={styles.content}>
          {renderContent()}
        </div>

        {/* Navigation hint */}
        {currentLayer < totalLayers && (
          <button onClick={goDeeper} style={styles.deeperButton(module.color)}>
            <span>Go Deeper</span>
            <ChevronDown size={20} />
          </button>
        )}

        {currentLayer === totalLayers && (
          <div style={styles.bottomReached}>
            <span style={{ color: module.color }}>You've reached the core</span>
            <p style={{ color: '#6b7280', fontSize: '14px', marginTop: '8px' }}>
              This is the actual code that powers this module
            </p>
          </div>
        )}
      </div>

      {/* CSS for animations */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) scale(1); }
          50% { transform: translateY(-20px) scale(1.1); }
        }
        @keyframes pulse-ring {
          0% { transform: translate(-50%, -50%) scale(1); opacity: 0.3; }
          100% { transform: translate(-50%, -50%) scale(1.5); opacity: 0; }
        }
        @keyframes particle-float {
          0%, 100% { transform: translateY(0) translateX(0); opacity: 0.3; }
          25% { transform: translateY(-30px) translateX(10px); opacity: 0.6; }
          50% { transform: translateY(-60px) translateX(-10px); opacity: 0.3; }
          75% { transform: translateY(-30px) translateX(5px); opacity: 0.6; }
        }
        @keyframes slideIn {
          from { transform: translateX(-20px); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

// Styles
const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 9999,  // Very high to cover site header
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#030712',
    overflowY: 'auto',  // Scrollable
    overflowX: 'hidden',
  },
  particleField: (color, intensity) => ({
    position: 'absolute',
    inset: 0,
    background: `radial-gradient(ellipse at center, ${color}10 0%, transparent 70%)`,
    opacity: 0.5 + intensity * 0.5,
  }),
  particle: {
    position: 'absolute',
    width: '4px',
    height: '4px',
    borderRadius: '50%',
    animation: 'particle-float 5s ease-in-out infinite',
  },
  depthRings: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    pointerEvents: 'none',
  },
  depthRing: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    borderRadius: '50%',
    border: '1px solid',
    animation: 'pulse-ring 4s ease-out infinite',
  },
  topNav: {
    position: 'fixed',  // Fixed so it stays when scrolling
    top: 0,
    left: 0,
    right: 0,
    padding: '16px 24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: 'rgba(3, 7, 18, 0.95)',
    backdropFilter: 'blur(12px)',
    borderBottom: '1px solid rgba(55, 65, 81, 0.5)',
    zIndex: 10000,  // Higher than overlay
  },
  topNavRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  topNavButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 14px',
    backgroundColor: 'rgba(31, 41, 55, 0.8)',
    border: '1px solid #374151',
    borderRadius: '8px',
    color: '#9ca3af',
    fontSize: '13px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  closeButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '36px',
    height: '36px',
    backgroundColor: 'rgba(31, 41, 55, 0.8)',
    border: '1px solid #374151',
    borderRadius: '8px',
    color: '#9ca3af',
    cursor: 'pointer',
  },
  breadcrumbs: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '13px',
  },
  breadcrumbItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 10px',
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: '6px',
    color: '#6b7280',
    fontSize: '13px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  breadcrumbHome: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 14px',
    backgroundColor: 'rgba(31, 41, 55, 0.8)',
    border: '1px solid #374151',
    borderRadius: '8px',
    color: '#e5e7eb',
    fontSize: '13px',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  breadcrumbLayer: {
    fontSize: '10px',
    padding: '2px 4px',
    backgroundColor: 'rgba(55, 65, 81, 0.5)',
    borderRadius: '3px',
    color: '#9ca3af',
  },
  flowGuide: {
    position: 'absolute',
    top: '70px',
    right: '24px',
    width: '280px',
    backgroundColor: 'rgba(17, 24, 39, 0.95)',
    borderRadius: '12px',
    border: '1px solid #374151',
    overflow: 'hidden',
    zIndex: 65,
    animation: 'slideIn 0.3s ease-out',
  },
  flowGuideHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 16px',
    backgroundColor: 'rgba(31, 41, 55, 0.5)',
    borderBottom: '1px solid #374151',
    color: '#e5e7eb',
    fontSize: '13px',
    fontWeight: 500,
  },
  flowGuideClose: {
    marginLeft: 'auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: '4px',
    color: '#6b7280',
    cursor: 'pointer',
  },
  flowGuideContent: {
    padding: '12px',
    maxHeight: '400px',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  flowStep: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '10px 12px',
    backgroundColor: 'transparent',
    border: '1px solid #374151',
    borderRadius: '8px',
    textAlign: 'left',
    transition: 'all 0.2s',
  },
  flowStepNumber: (isCurrent, isPast) => ({
    width: '20px',
    height: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    backgroundColor: isCurrent ? 'rgba(255,255,255,0.2)' : isPast ? 'rgba(34, 197, 94, 0.2)' : 'rgba(55, 65, 81, 0.5)',
    color: isCurrent ? '#fff' : isPast ? '#86efac' : '#6b7280',
    fontSize: '11px',
    fontWeight: 600,
  }),
  flowStepIcon: {
    fontSize: '16px',
  },
  flowStepTitle: {
    flex: 1,
    fontSize: '12px',
  },
  currentIndicator: {
    fontSize: '10px',
    color: '#86efac',
    fontStyle: 'italic',
  },
  historyBackButton: {
    position: 'absolute',
    top: '80px',
    left: '24px',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '10px 16px',
    backgroundColor: 'rgba(31, 41, 55, 0.8)',
    border: '1px solid #374151',
    borderRadius: '8px',
    color: '#9ca3af',
    fontSize: '13px',
    cursor: 'pointer',
    zIndex: 60,
  },
  backButton: {
    position: 'absolute',
    top: '130px',
    left: '24px',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '10px 16px',
    backgroundColor: 'rgba(31, 41, 55, 0.8)',
    border: '1px solid #374151',
    borderRadius: '8px',
    color: '#9ca3af',
    fontSize: '13px',
    cursor: 'pointer',
    zIndex: 60,
  },
  depthIndicator: {
    position: 'fixed',  // Fixed so it stays when scrolling
    right: '24px',
    top: '50%',
    transform: 'translateY(-50%)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
    zIndex: 10000,  // Same as navs
  },
  depthTrack: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  depthDot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    transition: 'all 0.3s',
    border: 'none',
  },
  depthLabel: {
    color: '#6b7280',
    fontSize: '12px',
    writingMode: 'vertical-rl',
    transform: 'rotate(180deg)',
  },
  leftPanel: {
    position: 'fixed',  // Fixed so it stays when scrolling
    left: '24px',
    top: '80px',  // Below topNav
    width: '300px',
    maxHeight: 'calc(100vh - 200px)',  // Account for top and bottom navs
    overflowY: 'auto',
    zIndex: 10000,  // Same as navs
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  relatedPanel: (isOpen) => ({
    backgroundColor: 'rgba(17, 24, 39, 0.9)',
    borderRadius: '12px',
    border: '1px solid #374151',
    overflow: 'hidden',
    transition: 'all 0.3s',
  }),
  tokenPanel: (isOpen) => ({
    backgroundColor: 'rgba(17, 24, 39, 0.9)',
    borderRadius: '12px',
    border: '1px solid #374151',
    overflow: 'hidden',
    transition: 'all 0.3s',
  }),
  panelToggle: (color) => ({
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '14px 16px',
    backgroundColor: 'transparent',
    border: 'none',
    color: '#e5e7eb',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.2s',
  }),
  relatedList: {
    padding: '0 12px 12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    animation: 'slideIn 0.3s ease-out',
  },
  relatedItem: (color, direction) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    padding: '12px',
    backgroundColor: 'rgba(31, 41, 55, 0.5)',
    border: `1px solid ${color}40`,
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    textAlign: 'left',
  }),
  relatedHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  relatedIcon: {
    fontSize: '20px',
  },
  relatedInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  relatedTitle: {
    color: '#e5e7eb',
    fontSize: '14px',
    fontWeight: 500,
  },
  relatedDirection: {
    color: '#6b7280',
    fontSize: '11px',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  relatedDescription: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
  },
  connectionBadge: {
    fontSize: '10px',
    fontWeight: 600,
    padding: '2px 8px',
    borderRadius: '4px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  dataFlow: {
    fontSize: '11px',
    color: '#9ca3af',
    fontFamily: 'monospace',
  },
  relatedDesc: {
    fontSize: '12px',
    color: '#9ca3af',
    margin: 0,
    lineHeight: 1.4,
  },
  tokenContent: {
    padding: '0 12px 12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    animation: 'slideIn 0.3s ease-out',
  },
  tokenSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  tokenLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '11px',
    fontWeight: 600,
    color: '#9ca3af',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  tokenValue: {
    fontSize: '13px',
    color: '#d1d5db',
    margin: 0,
    lineHeight: 1.5,
  },
  tokenList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
  },
  tokenChip: (type) => ({
    fontSize: '11px',
    padding: '4px 8px',
    borderRadius: '4px',
    backgroundColor: type === 'input' ? 'rgba(34, 197, 94, 0.2)' : 'rgba(59, 130, 246, 0.2)',
    color: type === 'input' ? '#86efac' : '#93c5fd',
    fontFamily: 'monospace',
  }),
  contentArea: {
    position: 'relative',
    zIndex: 55,
    maxWidth: '700px',
    width: '100%',
    padding: '40px',
    paddingTop: '100px',   // Space for fixed topNav
    paddingBottom: '180px', // Space for fixed bottomNav
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    transition: 'all 0.3s ease-out',
  },
  moduleIcon: (color) => ({
    fontSize: '64px',
    marginBottom: '16px',
    filter: `drop-shadow(0 0 30px ${color})`,
    animation: 'float 4s ease-in-out infinite',
  }),
  moduleTitle: {
    fontSize: '14px',
    color: '#9ca3af',
    marginBottom: '8px',
    fontWeight: 500,
  },
  layerType: (color) => ({
    fontSize: '12px',
    fontWeight: 700,
    letterSpacing: '3px',
    color: color,
    marginBottom: '16px',
    textTransform: 'uppercase',
  }),
  content: {
    maxWidth: '650px',
    width: '100%',
  },
  contentText: {
    fontSize: '24px',
    lineHeight: 1.6,
    color: '#e5e7eb',
    fontWeight: 300,
  },
  rulesList: {
    listStyle: 'none',
    padding: 0,
    textAlign: 'left',
  },
  ruleItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    marginBottom: '16px',
    color: '#d1d5db',
    fontSize: '18px',
    lineHeight: 1.6,
  },
  ruleBullet: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    marginTop: '10px',
    flexShrink: 0,
  },
  exampleContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    width: '100%',
  },
  exampleBox: (type) => ({
    backgroundColor: type === 'bad' ? 'rgba(127, 29, 29, 0.3)' : 'rgba(20, 83, 45, 0.3)',
    border: `1px solid ${type === 'bad' ? 'rgba(239, 68, 68, 0.3)' : 'rgba(34, 197, 94, 0.3)'}`,
    borderRadius: '12px',
    padding: '24px',
    textAlign: 'left',
  }),
  exampleLabel: (type) => ({
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '2px',
    color: type === 'bad' ? '#fca5a5' : '#86efac',
    marginBottom: '12px',
  }),
  exampleText: {
    color: '#e5e7eb',
    fontSize: '16px',
    lineHeight: 1.7,
    fontStyle: 'italic',
    margin: 0,
  },
  exampleArrow: {
    display: 'flex',
    justifyContent: 'center',
    padding: '8px',
  },
  codeContainer: {
    backgroundColor: '#0d1117',
    borderRadius: '12px',
    overflow: 'hidden',
    textAlign: 'left',
    width: '100%',
  },
  codeHeader: {
    backgroundColor: '#161b22',
    padding: '12px 16px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: '#9ca3af',
    fontSize: '13px',
    borderBottom: '1px solid #30363d',
  },
  codeBlock: {
    padding: '20px',
    margin: 0,
    fontSize: '13px',
    lineHeight: 1.6,
    color: '#c9d1d9',
    overflowX: 'auto',
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
  },
  deeperButton: (color) => ({
    marginTop: '48px',
    background: `linear-gradient(135deg, ${color}40, ${color}20)`,
    border: `1px solid ${color}60`,
    borderRadius: '100px',
    padding: '16px 32px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    cursor: 'pointer',
    color: 'white',
    fontSize: '16px',
    fontWeight: 500,
    transition: 'all 0.3s',
    animation: 'float 3s ease-in-out infinite',
  }),
  bottomReached: {
    marginTop: '48px',
    padding: '24px',
    borderRadius: '12px',
    backgroundColor: 'rgba(31, 41, 55, 0.5)',
    textAlign: 'center',
  },
  followFlowButton: (color) => ({
    marginTop: '16px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 20px',
    backgroundColor: `${color}30`,
    border: `1px solid ${color}60`,
    borderRadius: '8px',
    color: '#e5e7eb',
    fontSize: '14px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  }),
  bottomNav: {
    position: 'fixed',  // Fixed so it stays when scrolling
    bottom: 0,
    left: 0,
    right: 0,
    padding: '16px 24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    alignItems: 'center',
    backgroundColor: 'rgba(3, 7, 18, 0.95)',
    backdropFilter: 'blur(12px)',
    borderTop: '1px solid rgba(55, 65, 81, 0.5)',
    zIndex: 10000,  // Same as topNav
  },
  flowNav: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  flowNavButton: (color) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 16px',
    backgroundColor: `${color}20`,
    border: `1px solid ${color}40`,
    borderRadius: '8px',
    color: '#e5e7eb',
    fontSize: '13px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  }),
  flowNavCurrent: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 16px',
    color: '#6b7280',
    fontSize: '12px',
  },
  quickNav: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  quickNavLabel: {
    fontSize: '12px',
    color: '#6b7280',
    marginRight: '4px',
  },
  quickNavButton: (color) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    backgroundColor: `${color}15`,
    border: `1px solid ${color}30`,
    borderRadius: '6px',
    color: '#d1d5db',
    fontSize: '11px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  }),
  keyboardHints: {
    position: 'absolute',
    bottom: '100px',
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    gap: '16px',
    color: '#4b5563',
    fontSize: '11px',
    zIndex: 55,
  },
  kbd: {
    display: 'inline-block',
    padding: '2px 5px',
    backgroundColor: '#1f2937',
    borderRadius: '3px',
    border: '1px solid #374151',
    fontSize: '10px',
    fontFamily: 'monospace',
    marginRight: '2px',
  },
  layerJump: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  layerJumpButton: {
    width: '24px',
    height: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '4px',
    border: '1px solid #374151',
    fontSize: '11px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
};

// Add ChevronRight to imports for breadcrumbs
const ChevronRight = ({ size = 16, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6"></polyline>
  </svg>
);

export default LayerView;
